import {
  LeaderBoardStatusType,
  LeagueType,
  bronzeLeagueTopPlayersCount,
  diamondLeagueTopPlayersCount,
  goldLeagueTopPlayersCount,
  leaguesWeightMap,
  losePlayersCount,
  silverLeagueTopPlayersCount,
} from './LeaderBoardStore.constants';
import {
  LeaderBoardContent,
  LeaderBoardContentActive,
  LeaderBoardContentNew,
  LeaderBoardContentPending,
  LeaderBoardState,
  LeagueProps,
  Player,
  PlayerForUI,
  PlayersListForUI,
} from './LeaderBoardStore.types';

import {Store} from '../Store';

export class LeaderBoardStore extends Store<LeaderBoardState> {
  constructor() {
    super({
      nextRecalculationAt: null,
      timerToRecalculateLeagueValue: '',
      activeLeagueTab: null,
      playerLeague: null,
      playerScore: 0,
      content: {
        status: LeaderBoardStatusType.New,
      },
    });
  }

  public setNextRecalculationAt = (nextRecalculationAt: string): void => {
    this.setValue('nextRecalculationAt', nextRecalculationAt);
  };

  public setActiveLeagueTab = (activeLeagueTab: LeagueType): void => {
    this.setValue('activeLeagueTab', activeLeagueTab);
  };

  public setPlayerLeague = (playerLeague: LeagueType | null): void => {
    this.setValue('playerLeague', playerLeague);
    this.setValue('activeLeagueTab', playerLeague);
  };

  public setPlayerScore = (playerScore: number): void => {
    this.setValue('playerScore', playerScore);
  };

  public setContent = (content: LeaderBoardContent): void => {
    this.setValue('content', content);
  };

  public setTimerToRecalculateLeagueValue = (value: string): void => {
    this.setValue('timerToRecalculateLeagueValue', value);
  };

  public getNextRecalculationAt = (): string | null => {
    return this.getState().nextRecalculationAt;
  };

  public getTimerToRecalculateLeagueValue = (): string => {
    return this.getState().timerToRecalculateLeagueValue;
  };

  public getActiveLeagueTab = (): LeagueType | null => {
    const {activeLeagueTab, content} = this.getState();

    // Если пользователь еще не определен в корзину, то открываем ему бронзовую лигу
    if (!activeLeagueTab && content.status !== LeaderBoardStatusType.Active) {
      return LeagueType.Bronze;
    }
    return activeLeagueTab;
  };

  public getPlayerLeague = (): LeagueType | null => {
    return this.getState().playerLeague;
  };

  public getPlayerLeagueForHeader = (): LeagueType => {
    return this.getPlayerLeague() || LeagueType.Bronze;
  };

  public getPlayerScore = (): number => {
    return this.getState().playerScore;
  };

  public getContent = (): LeaderBoardContent => {
    return this.getState().content;
  };

  public getPlayersListForUI = (playersList: Player[], isGameFinished: boolean): PlayersListForUI => {
    const sortedPlayersList = this.sortPlayersListByRank(playersList);
    const playerLeague = this.getPlayerLeague();
    const lastMiddlePlayerPlace = sortedPlayersList.length - losePlayersCount;

    let topPlayersCount = 0;
    switch (playerLeague) {
      case LeagueType.Bronze:
        topPlayersCount = bronzeLeagueTopPlayersCount;
        break;
      case LeagueType.Silver:
        topPlayersCount = silverLeagueTopPlayersCount;
        break;
      case LeagueType.Gold:
        topPlayersCount = goldLeagueTopPlayersCount;
        break;
      case LeagueType.Diamond:
        topPlayersCount = diamondLeagueTopPlayersCount;
        break;
    }

    // Для бронзовой лиги и после окончания активной стадии не формируем места на понижение
    const isPushedPlayerToBottom = playerLeague !== LeagueType.Bronze && !isGameFinished;

    return sortedPlayersList.reduce<PlayersListForUI>(
      (acc, player) => {
        const playerForUI: PlayerForUI = {
          ...player,
          name: `${player.firstName} ${player.lastName}`,
          place: player.rank,
        };

        if (player.rank <= topPlayersCount) {
          acc.top.push(playerForUI);
        } else if (player.rank > lastMiddlePlayerPlace && isPushedPlayerToBottom) {
          acc.bottom.push(playerForUI);
        } else {
          acc.middle.push(playerForUI);
        }

        return acc;
      },
      {
        top: [],
        middle: [],
        bottom: [],
      },
    );
  };

  public static isContentTypeNew = (content: LeaderBoardContent): content is LeaderBoardContentNew => {
    return content.status === LeaderBoardStatusType.New;
  };

  public static isContentTypePending = (content: LeaderBoardContent): content is LeaderBoardContentPending => {
    return content.status === LeaderBoardStatusType.Pending;
  };

  public static isContentTypeActive = (content: LeaderBoardContent): content is LeaderBoardContentActive => {
    return content.status === LeaderBoardStatusType.Active;
  };

  public static isLeagueTypeBronze = (leagueType: LeagueType): boolean => {
    return leagueType === LeagueType.Bronze;
  };

  public static isLeagueTypeSilver = (leagueType: LeagueType): boolean => {
    return leagueType === LeagueType.Silver;
  };

  public static isLeagueTypeGold = (leagueType: LeagueType): boolean => {
    return leagueType === LeagueType.Gold;
  };

  public static isLeagueTypeDiamond = (leagueType: LeagueType): boolean => {
    return leagueType === LeagueType.Diamond;
  };

  public static needInitLeague = (leagueProps: LeagueProps): boolean => {
    return leagueProps.league === null && leagueProps.score > 0;
  };

  public isLeagueBlocked = (league: LeagueType): boolean => {
    const playerLeague = this.getPlayerLeague();

    if (!playerLeague) {
      return true;
    }

    return leaguesWeightMap[league] > leaguesWeightMap[playerLeague];
  };

  public isPreviousLeague = (league: LeagueType): boolean => {
    const playerLeague = this.getPlayerLeague();

    if (!playerLeague) {
      return false;
    }

    return leaguesWeightMap[league] < leaguesWeightMap[playerLeague];
  };

  public isPlayerLeagueActiveTab = (): boolean => {
    return this.getActiveLeagueTab() === this.getPlayerLeague();
  };

  public isActiveLeagueTabBronze = (): boolean => {
    return this.getActiveLeagueTab() === LeagueType.Bronze;
  };

  public isActiveLeagueTabDiamond = (): boolean => {
    return this.getActiveLeagueTab() === LeagueType.Diamond;
  };

  private sortPlayersListByRank = (playersList: Player[]): Player[] => {
    return playersList.slice().sort((a, b) => a.rank - b.rank);
  };
}
